package com.ricky.realtimeledger.pages

import androidx.compose.runtime.*
import com.ricky.realtimeledger.theme.toSitePalette
import com.ricky.realtimeledger.utils.Res
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.w3c.fetch.NO_CORS
import org.w3c.fetch.RequestInit

private const val GOOGLE_SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbw3L96QMc2dJc2SdWAYxn2DQ0pAXUVlmsrjoYnqZLz6chWm8dNYnZbi4VYcUVBou20FPQ/exec"

@Page
@Composable
fun HomePage() {
    val sitePalette = ColorMode.current.toSitePalette()

    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var contact by remember { mutableStateOf("") }
    var honeypot by remember { mutableStateOf("") }
    var showModal by remember { mutableStateOf(false) }

    // Wrapped the entire page in a Box to allow for the Modal overlay
    Box(modifier = Modifier.fillMaxSize()) {

        SimpleGrid(
            numColumns = numColumns(1, md = 2),
            modifier = Modifier.fillMaxSize().minHeight(100.vh).backgroundColor(Colors.White)
        ) {
            // --- LEFT COLUMN: CONTENT & FORM PANEL ---
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    // Reduced top padding to 2.em and side padding to 5% to bring the logo closer to the edges
                    .padding(top = 2.em, bottom = 4.em, leftRight = 5.percent)
                    .minHeight(100.vh),
                verticalArrangement = Arrangement.SpaceBetween,
                horizontalAlignment = Alignment.Start
            ) {

                Image(
                    src = Res.Images.LOGO,
                    description = "Realtime Ledger Logo Banner",
                    modifier = Modifier.maxWidth(220.px).fillMaxWidth()
                )

                Column(
                    modifier = Modifier.fillMaxWidth().margin(topBottom = 2.em),
                    verticalArrangement = Arrangement.spacedBy(1.8.em)
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(0.5.em)) {
                        SpanText(
                            text = "Be Right Back. Probably.", // Text Updated
                            modifier = Modifier.color(sitePalette.custom.darkText).fontSize(3.5.em)
                                .fontWeight(FontWeight.Normal).styleModifier {
                                    fontFamily("Playfair Display", "Georgia", "serif")
                                    fontStyle("italic")
                                })
                        SpanText(
                            // Message Updated
                            text = "Just kidding, we definitely will be. We're tuning up the ledger engines. Drop your email and we'll ping you very soon.",
                            modifier = Modifier.color(Colors.Black).fontSize(1.05.em).maxWidth(400.px)
                        )
                    }

                    // Form no longer hides, it just clears its values
                    Column(
                        modifier = Modifier.maxWidth(400.px).fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(1.em)
                    ) {
                        Input(
                            type = InputType.Text,
                            attrs = Modifier
                                .opacity(0)
                                .position(Position.Absolute)
                                .top(0.px)
                                .left(0.px)
                                .width(1.px)
                                .height(1.px)
                                .styleModifier { property("pointer-events", "none") }
                                .toAttrs {
                                    attr("name", "website_url_honeypot")
                                    attr("tabindex", "-1")
                                    attr("autocomplete", "off")
                                    value(honeypot)
                                    onInput { honeypot = it.value }
                                }
                        )

                        InputField(
                            type = InputType.Text,
                            placeholder = "Your Name",
                            value = name,
                            onValueChange = { name = it })

                        InputField(
                            type = InputType.Email,
                            placeholder = "Email Address",
                            value = email,
                            onValueChange = { email = it })

                        InputField(
                            type = InputType.Tel,
                            placeholder = "Contact Number",
                            value = contact,
                            onValueChange = { contact = it })

                        Button(
                            onClick = {
                                if (honeypot.isNotEmpty()) {
                                    showModal = true
                                    name = ""
                                    email = ""
                                    contact = ""
                                    honeypot = ""
                                    return@Button
                                }

                                if (name.isNotBlank() && email.isNotBlank()) {
                                    val payload = kotlin.js.json(
                                        "name" to name, "email" to email, "contact" to contact
                                    )
                                    val jsonString = JSON.stringify(payload)

                                    val requestOptions = RequestInit(
                                        method = "POST",
                                        body = jsonString,
                                        headers = kotlin.js.json("Content-Type" to "text/plain;charset=utf-8"),
                                        mode = org.w3c.fetch.RequestMode.NO_CORS
                                    )

                                    window.fetch(GOOGLE_SCRIPT_URL, requestOptions)

                                    // Trigger Modal and Clear Form
                                    showModal = true
                                    name = ""
                                    email = ""
                                    contact = ""
                                }
                            },
                            modifier = Modifier.fillMaxWidth().height(48.px).backgroundColor(sitePalette.brand.primary)
                                .color(Colors.White).borderRadius(8.px).fontWeight(FontWeight.Bold)
                                .cursor(Cursor.Pointer).boxShadow(
                                    0.px, 4.px, 12.px, color = sitePalette.brand.primary.toRgb().copyf(alpha = 0.25f)
                                )
                        ) {
                            Text("Send") // Button Text Updated
                        }
                    }
                }

                Row(
                    horizontalArrangement = Arrangement.spacedBy(1.2.em), verticalAlignment = Alignment.CenterVertically
                ) {
                    SocialIconLink(href = "https://twitter.com") {
                        SvgIcon(d = "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z")
                    }
                    SocialIconLink(href = "https://linkedin.com") {
                        SvgIcon(d = "M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z")
                    }
                    SocialIconLink(href = "mailto:info@realtimeledger.ca") {
                        SvgIcon(d = "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z")
                    }
                }
            }

            Box(
                modifier = Modifier.fillMaxSize().minHeight(100.vh).displayIfAtLeast(Breakpoint.MD).styleModifier {
                    property(
                        "background-image",
                        "linear-gradient(to bottom, rgba(20, 15, 12, 0.25), rgba(6, 8, 11, 0.4)), url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1200')"
                    )
                    property("background-size", "cover")
                    property("background-position", "center")
                })
        }

        // --- SUCCESS MODAL OVERLAY ---
        if (showModal) {
            Box(
                modifier = Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px)
                    .fillMaxSize()
                    .backgroundColor(Colors.Black.toRgb().copyf(alpha = 0.5f))
                    .zIndex(1000)
                    .onClick { showModal = false }, // Close modal if clicked outside
                contentAlignment = Alignment.Center
            ) {
                // Modal Content Card
                Column(
                    modifier = Modifier
                        .maxWidth(400.px)
                        .fillMaxWidth(90.percent)
                        .backgroundColor(Colors.White)
                        .borderRadius(12.px)
                        .padding(2.5.em)
                        .boxShadow(0.px, 10.px, 30.px, color = Colors.Black.toRgb().copyf(alpha = 0.2f))
                        .onClick { it.stopPropagation() }, // Prevent clicks inside the card from closing it
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(1.em)
                ) {
                    SpanText(
                        text = "Message Sent!",
                        modifier = Modifier.fontSize(1.5.em).fontWeight(FontWeight.Bold).color(sitePalette.brand.primary)
                    )
                    SpanText(
                        text = "Thank you. We have received your details and will be in touch shortly.",
                        modifier = Modifier.color(Colors.DarkGray).textAlign(TextAlign.Center).lineHeight(1.5)
                    )
                    Button(
                        onClick = { showModal = false },
                        modifier = Modifier
                            .margin(top = 1.em)
                            .fillMaxWidth()
                            .height(44.px)
                            .backgroundColor(sitePalette.brand.primary)
                            .color(Colors.White)
                            .borderRadius(8.px)
                            .fontWeight(FontWeight.Bold)
                            .cursor(Cursor.Pointer)
                    ) {
                        Text("Okay")
                    }
                }
            }
        }
    }
}

@Composable
fun InputField(
    type: InputType<String>, placeholder: String, value: String, onValueChange: (String) -> Unit
) {
    Input(
        type = type,
        attrs = Modifier
            .fillMaxWidth()
            .height(52.px)
            .padding(leftRight = 16.px)
            .borderRadius(8.px)
            .backgroundColor(Color.rgb(0xF8F9FA))
            .border(1.px, LineStyle.Solid, Color.rgb(0xE2E8F0))
            .boxShadow(0.px, 3.px, 8.px, color = Colors.Black.toRgb().copyf(alpha = 0.04f))
            .outline(0.px, LineStyle.None, Colors.Transparent)
            .fontSize(0.95.em)
            .styleModifier {
                property("transition", "border-color 0.2s, background-color 0.2s")
            }
            .toAttrs {
                placeholder(placeholder)
                value(value)
                onInput { onValueChange(it.value) }
            })
}

@Composable
fun SvgIcon(d: String) {
    Svg(attrs = Modifier.width(20.px).height(20.px).toAttrs {
        viewBox(0, 0, 24, 24)
    }) {
        Path(attrs = {
            attr("d", d)
            attr("fill", "currentColor")
        })
    }
}

@Composable
fun SocialIconLink(
    href: String, icon: @Composable () -> Unit
) {
    val sitePalette = ColorMode.current.toSitePalette()
    var isHovered by remember { mutableStateOf(false) }

    Link(path = href) {
        Box(modifier = Modifier.color(if (isHovered) sitePalette.brand.primary else Color.rgb(0x9E9E9E)).styleModifier {
            property("transition", "color 200ms, transform 200ms")
        }.onMouseEnter { isHovered = true }.onMouseLeave { isHovered = false }.cursor(Cursor.Pointer)) {
            icon()
        }
    }
}